/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { Button} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import * as XLSX from 'xlsx';
import { Info, FileText, Upload, CheckCircle, XCircle, Loader,  AlertTriangle } from 'lucide-react';
import adminServices from '../../../services/adminServices';
import DialogModal from '../../../components/modal/DialogModal';


const BulkImportQuestions = () => {
    const { t, i18n } = useTranslation('contentCreatorBulkUpload');
    const questionsPerPage = 5;
    const [assessmentTypes, setAssessmentTypes] = useState([]);
    const [errors, setErrors] = useState('');
    const userId = useSelector((state) => state.userInfo && state.userInfo.id);
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState('');
    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [questions, setQuestions] = useState([]);
    console.log(questions, "questionsquestions");


    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarTitle, setSnackbarTitle] = useState('');
    const [snackbarType, setSnackbarType] = useState('success');

    // Failed Questions Dialog State
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [importResults, setImportResults] = useState(null);

    useEffect(() => {
        fetchModules();
    }, []);

    const totalPages = Math.ceil(questions.length / questionsPerPage);
    const currentQuestions = questions.slice(
        (currentPage - 1) * questionsPerPage,
        currentPage * questionsPerPage
    );

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };



    const fetchModules = async (assessmentTypeId) => {
        try {
            setLoading(true);
            setSources([]);
            setSelectedSource('');
            setChapters([]);
            setSelectedChapter('');

            const result = await adminServices.getModulesDropdown(assessmentTypeId);
            if (result?.data?.data) {
                const formattedSources = result.data.data.map(item => ({
                    value: item.id,
                    label: item.title || item.name
                }));
                setSources(formattedSources);
            }
        } catch (error) {
            console.error('Error fetching modules:', error);
            showSnackbar(t('errors.loadingSources'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchChapters = async (sourceId) => {
        try {
            setLoading(true);
            setChapters([]);
            setSelectedChapter('');

            const result = await adminServices.getChapterDropdown(sourceId);
            if (result?.data?.data) {
                const formattedChapters = result.data.data.map(item => ({
                    value: item.id,
                    label: item.module_name || item.name || item.title
                }));
                setChapters(formattedChapters);
            }
        } catch (error) {
            console.error('Error fetching chapters:', error);
            showSnackbar(t('errors.loadingChapters'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSourceChange = (e) => {
        const sourceId = e.target.value;
        setSelectedSource(sourceId);
        if (sourceId) {
            fetchChapters(sourceId);
        } else {
            setChapters([]);
            setSelectedChapter('');
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            setErrors('');
            const validExtensions = ['.csv', '.xlsx', '.xls'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                showSnackbar(t('errors.invalidFileFormat'), 'error');
                event.target.value = '';
                return;
            }

            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                showSnackbar(t('errors.fileSizeExceeded'), 'error');
                event.target.value = '';
                return;
            }

            setSelectedFile(file);
            setFileName(file.name);
            setCurrentPage(1)
            const extractedQuestions = await processExcel(file);
            setQuestions(extractedQuestions);
        }
    };
    const processExcel = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

                if (!jsonData.length) {
                    throw new Error('No data found in the Excel file');
                }
                console.log(jsonData, "jsonDatajsonData");

                const formattedQuestions = jsonData.map((row) => ({
                    question_text: row.question_text || '',
                    question_type: row.question_type || '',
                    acceptable_answers: row.acceptable_answers || '',
                    answer_type: row.answer_type || '',
                    correct_answer: row.correct_answer || '',
                    difficulty_tag: row.difficulty_tag || '',
                    explanation_text: row.explanation_text || '',
                    explanation_video_url: row.explanation_video_url || '',
                    marks: row.marks || '',
                    negative_marks: row.negative_marks || '',
                    option_a: row.option_a || '',
                    option_b: row.option_b || '',
                    option_c: row.option_c || '',
                    option_d: row.option_d || '',
                    sample_answer: row.sample_answer || '',
                    status: row.status || '',

                }));

                resolve(formattedQuestions);
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => {
            reject(new Error('Error reading file'));
        };

        reader.readAsArrayBuffer(file);
    });

    const createSafeHTML = (htmlContent) => {
        if (!htmlContent) return { __html: '' };
        const cleanHTML = DOMPurify.sanitize(htmlContent, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', /* etc */],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style']
        });
        return { __html: cleanHTML };
    };
    const showSnackbar = (message, type = 'success') => {
        setSnackbarTitle(message);
        setSnackbarType(type);
        setOpenSnackbar(true);
        setTimeout(() => setOpenSnackbar(false), 5000);
    };

    const handleDownloadTemplate = () => {
        const headers = [
            'question_text', 'question_type', 'difficulty_tag', 'marks', 'negative_marks',
            'explanation_text', 'explanation_video_url', 'option_a', 'option_b', 'option_c',
            'option_d', 'correct_answer', 'correct_answers', 'marks_per_correct',
            'negative_marks_per_incorrect', 'answer_type', 'acceptable_answers',
            'min_value', 'max_value', 'sample_answer', 'status'
        ];

        const sampleRow = [
            'What is 2+2?', 'MCQ', 'EASY', '1', '0', 'Simple addition problem', '',
            '3', '4', '5', '6', 'B', '', '', '', '', '', '', '', '', 'ACTIVE'
        ];

        const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'bulk_import_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSnackbar(t('messages.templateDownloaded'), 'success');
    };

    const validateImportData = () => {
        const errors = [];

        if (!selectedSource) errors.push(t('validation.selectSource'));
        if (!selectedChapter) errors.push(t('validation.selectChapter'));
        if (!selectedFile) errors.push(t('validation.selectFile'));

        if (selectedFile) {
            const validExtensions = ['.csv', '.xlsx', '.xls'];
            const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                errors.push(t('validation.invalidFileFormat'));
            }

            const maxSize = 10 * 1024 * 1024;
            if (selectedFile.size > maxSize) {
                errors.push(t('validation.fileSizeExceeded'));
            }
        }

        return errors;
    };

    const handleImportQuestions = async () => {
        const validationErrors = validateImportData();

        if (validationErrors.length > 0) {
            showSnackbar(validationErrors[0], 'error');
            return;
        }

        try {
            setUploadLoading(true);

            const formData = new FormData();
            formData.append('questionFile', selectedFile);
            formData.append('assessment_type_id', selectedAssessmentType);
            formData.append('source_id', selectedSource);
            formData.append('chapter_id', selectedChapter);

            const result = await adminServices.SatQuestionupload(formData, userId);
            console.log(result,"resultresult");
            

            if (result?.data?.success) {
                setErrors('');
                const data = result.data.data;
                setImportResults(data);
                setQuestions([])
                setCurrentPage(1)
                if (data.failed_count > 0) {
                    showSnackbar(
                        t('messages.partialSuccess', {
                            imported: data.imported_count,
                            total: data.total_questions,
                            failed: data.failed_count
                        }),
                        'warning'
                    );
                    setShowFailedDialog(true);
                } else {
                    showSnackbar(
                        t('messages.fullSuccess', { count: data.imported_count }),
                        'success'
                    );
                }

                setSelectedFile(null);
                setFileName('');
                const fileInput = document.getElementById('csv-file-input');
                if (fileInput) fileInput.value = '';
            } else {
                if (result?.data?.failedQuestions) {
                    setErrors(result.data); 
                    
                    setQuestions([]);
                }
                showSnackbar(result?.data?.message || t('errors.importFailed'), 'error');
            }
        } catch (error) {
            console.error('Error importing questions:', error);
            if (error?.response?.data?.failedQuestions) {
                setErrors(error.response.data);
            }
            const errorMessage = error?.response?.data?.message ||
                error?.message ||
                t('errors.importError');
            showSnackbar(errorMessage, 'error');
        } finally {
            setUploadLoading(false);
        }
    };

    const closeFailedDialog = () => {
        setShowFailedDialog(false);
        setImportResults(null);
    };

    const downloadFailedQuestions = () => {
        if (!importResults?.failed_questions || importResults.failed_questions.length === 0) {
            return;
        }

        const headers = [t('dialog.rowNumber'), t('dialog.questionText'), t('dialog.errorReason')];
        const rows = importResults.failed_questions.map(q => [
            q.rowIndex,
            `"${q.question_text?.replace(/"/g, '""') || 'N/A'}"`,
            `"${q.error?.replace(/"/g, '""') || t('dialog.unknownError')}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `failed_questions_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSnackbar(t('messages.failedQuestionsDownloaded'), 'success');
    };

    return (

        <div id="bulk-upload-page" className="min-h-screen bg-gray-50 p-4 sm:p-6 mt-12 lg:p-8">
            <div id="bulk-upload-wrapper" className=" mx-auto">
                {openSnackbar && (
                    <div id="bulk-upload-snackbar" className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transition-all ${snackbarType === 'success'
                        ? 'bg-green-500 text-white'
                        : snackbarType === 'warning'
                            ? 'bg-orange-500 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                        {snackbarType === 'success' ? (
                            <CheckCircle className="w-5 h-5" />
                        ) : snackbarType === 'warning' ? (
                            <AlertTriangle className="w-5 h-5" />
                        ) : (
                            <XCircle className="w-5 h-5" />
                        )}
                        <span className="text-sm font-medium">{snackbarTitle}</span>
                        <button
                            id="bulk-upload-snackbar-close-btn"
                            onClick={() => setOpenSnackbar(false)}
                            className="ml-2 hover:bg-white/20 rounded p-1"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Failed Questions Dialog */}
                <DialogModal
                    open={showFailedDialog}
                    handleClose={closeFailedDialog}
                    title={t('dialog.title')}
                    maxWidth="lg"
                >
                    {importResults && (
                        <div id="bulk-upload-dialog-content" className="flex flex-col max-h-[70vh]">
                            {/* Summary Header */}
                            <div id="bulk-upload-dialog-summary" className="px-6 pt-4 pb-2">
                                <p className="text-sm text-gray-600">
                                    {t('dialog.summary', {
                                        imported: importResults.imported_count,
                                        failed: importResults.failed_count
                                    })}
                                </p>
                            </div>

                            {/* Dialog Content */}
                            <div id="bulk-upload-dialog-body" className="flex-1 overflow-y-auto px-6 py-4">
                                {/* Summary Stats */}
                                <div id="bulk-upload-dialog-stats" className="grid grid-cols-3 gap-4 mb-6">
                                    <div id="bulk-upload-dialog-total" className="bg-blue-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {importResults.total_questions}
                                        </div>
                                        <div className="text-sm text-gray-600">{t('dialog.totalQuestions')}</div>
                                    </div>
                                    <div id="bulk-upload-dialog-imported" className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {importResults.imported_count}
                                        </div>
                                        <div className="text-sm text-gray-600">{t('dialog.imported')}</div>
                                    </div>
                                    <div id="bulk-upload-dialog-failed" className="bg-red-50 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-red-600">
                                            {importResults.failed_count}
                                        </div>
                                        <div className="text-sm text-gray-600">{t('dialog.failed')}</div>
                                    </div>
                                </div>

                                {/* Failed Questions List */}
                                {importResults.failed_questions && importResults.failed_questions.length > 0 && (
                                    <div id="bulk-upload-dialog-failed-list">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                            {t('dialog.failedQuestionsDetails')}
                                        </h3>
                                        <div className="space-y-4">
                                            {importResults.failed_questions.map((question, index) => (
                                                <div
                                                    key={index}
                                                    id={`bulk-upload-dialog-failed-item-${index}`}
                                                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="flex-shrink-0 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                                                            {question.rowIndex}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div
                                                                className="prose prose-sm max-w-none"
                                                                dangerouslySetInnerHTML={createSafeHTML(question.question_text)}
                                                            />
                                                            <div className="flex items-start gap-2">
                                                                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                                                <span className="text-sm text-red-700">
                                                                    {question.error || t('dialog.unknownError')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dialog Footer */}
                            <div id="bulk-upload-dialog-footer" className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                                <button
                                    id="bulk-upload-dialog-download-failed-btn"
                                    onClick={downloadFailedQuestions}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    {t('dialog.downloadFailed')}
                                </button>
                                <button
                                    id="bulk-upload-dialog-close-btn"
                                    onClick={closeFailedDialog}
                                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                    {t('dialog.close')}
                                </button>
                            </div>
                        </div>
                    )}
                </DialogModal>

                {/* Header Section */}
                <div id="bulk-upload-header" className="mb-6 sm:mb-8">
                    <h1 id="bulk-upload-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        {t('header.title')}
                    </h1>
                    <p id="bulk-upload-subtitle" className="text-sm sm:text-base text-gray-600">
                        {t('header.subtitle')}
                    </p>
                </div>

                {/* Info Banner */}
                <div id="bulk-upload-info-banner" className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 sm:mb-8">
                    <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm text-gray-700 break-words">
                                <span className="font-semibold">{t('infoBanner.supportedFormats')}</span>{' '}
                                {t('infoBanner.formatDetails')}{' '}

                            </p>
                        </div>
                    </div>
                </div>

                {/* Import Configuration Card */}
                <div id="bulk-upload-config-card" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
                    <h2 id="bulk-upload-config-title" className="text-lg sm:text-xl font-bold text-gray-900 mb-6">
                        {t('configCard.title')}
                    </h2>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            {/* Source Dropdown */}
                            <div>
                                <label htmlFor="source-select" className="block text-sm font-medium text-gray-900 mb-2">
                                    {t('configCard.source')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="source-select"
                                    value={selectedSource}
                                    onChange={handleSourceChange}
                                    disabled={loading}
                                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white text-gray-700 appearance-none cursor-pointer hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    <option value="">{t('configCard.selectSource')}</option>
                                    {sources.map((source) => (
                                        <option key={source.value} value={source.value}>
                                            {source.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Chapter Dropdown */}
                            <div>
                                <label htmlFor="chapter-select" className="block text-sm font-medium text-gray-900 mb-2">
                                    {t('configCard.chapter')} <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="chapter-select"
                                    value={selectedChapter}
                                    onChange={(e) => setSelectedChapter(e.target.value)}
                                    disabled={!selectedSource || loading}
                                    className="w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white text-gray-700 appearance-none cursor-pointer hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    <option value="">{t('configCard.selectChapter')}</option>
                                    {chapters.map((chapter) => (
                                        <option key={chapter.value} value={chapter.value}>
                                            {chapter.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* File Upload */}
                        <div>
                            <div className="block text-sm font-medium text-gray-900 mb-2">
                                {t('configCard.uploadFile')} <span className="text-red-500">*</span>
                                <span className="text-xs text-gray-500 ml-2">
                                    {t('configCard.fileLimit')}
                                </span>
                            </div>
                            <div className="relative">
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="csv-file-input"
                                    aria-label="File Upload"
                                />
                                <label
                                    htmlFor="csv-file-input"
                                    className="flex items-center justify-between w-full px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-all bg-white"
                                >
                                    <span className="text-gray-700 truncate flex-1 mr-2">
                                        {fileName || t('configCard.chooseFile')}
                                    </span>
                                    <span className="text-gray-500 text-sm flex-shrink-0">
                                        {fileName ? t('configCard.fileSelected') : t('configCard.noFileChosen')}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div id="bulk-upload-action-buttons" className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 mt-8">
                        <button
                            id="bulk-upload-download-template-btn"
                            onClick={handleDownloadTemplate}
                            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 order-2 sm:order-1"
                        >
                            <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                            {t('buttons.downloadTemplate')}
                        </button>
                        <button
                            id="bulk-upload-import-btn"
                            onClick={handleImportQuestions}
                            disabled={uploadLoading}
                            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 order-1 sm:order-2 disabled:bg-purple-400 disabled:cursor-not-allowed"
                        >
                            {uploadLoading ? (
                                <>
                                    <Loader className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                    {t('buttons.importing')}
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                                    {t('buttons.importQuestions')}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {questions.length > 0 && (

                    <>

                        <div id="bulk-upload-preview-section">
                            <div id="bulk-upload-preview-table-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
                                <table
                                    id="QuestionListtable"
                                    style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '50px' }}>No</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '300px' }}>Question</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Question Type</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Level</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Negative Marks</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Marks</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Option A</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Option B</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Option C</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Option D</th>

                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '100px' }}>Correct Answer</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '350px' }}>Explaination</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                       {currentQuestions.map((q, index) => (
                                            <tr key={index}>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>
                                                    {(currentPage - 1) * questionsPerPage + index + 1}
                                                </td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', }}>{q?.question_text}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q.question_type}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.difficulty_tag}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.negative_marks}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.marks}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.option_a}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.option_b}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.option_c}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: "center" }}>{q?.option_d}</td>

                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{q?.correct_answer}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{q?.explanation_text}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>


                            <div id="bulk-upload-pagination" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Button
                                    id="bulk-upload-pagination-prev-btn"
                                    variant="outlined"
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    style={{ cursor: 'pointer', }}
                                >
                                    <ArrowBackIosIcon style={{ fontSize: '16px', fontWeight: '800' }} />
                                    {/* <ArrowBackIcon/> */}
                                </Button>
                                <span id="bulk-upload-pagination-info" style={{ margin: '0 10px' }}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    id="bulk-upload-pagination-next-btn"
                                    variant="outlined"
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    style={{ height: '26px', cursor: 'pointer' }}
                                >
                                    <NavigateNextIcon style={{ fontSize: '25px', fontWeight: '400' }} />
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {errors && errors.message === 'Internal server error' ? (
                    <div id="bulk-upload-server-error" style={{ color: 'red', margin: '20px 0px', padding: '10px', border: '1px solid #ff4d4d', backgroundColor: '#fff0f0' }}>
                        <h3 style={{ margin: '0' }}>Error: {errors.error}</h3>
                    </div>
                ) : (
                    errors?.failedQuestions?.length > 0 && (
                        <div id="bulk-upload-failed-questions-section">
                            <div id="bulk-upload-failed-questions-table-wrapper" style={{ overflowX: 'auto', width: '100%' }}>
                                <table
                                    id="QuestionListtable"
                                    style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '50px' }}>No</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '300px' }}>Question</th>
                                            <th style={{ padding: '10px', border: '1px solid rgb(201 199 199)', minWidth: '350px' }}>Failures</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {errors.failedQuestions.map((q, index) => (
                                            <tr key={index}>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{index + 1}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{q.question}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>
                                                    {q.issues?.map((issue, i) => (
                                                        <div key={i} style={{ marginBottom: '4px', color: 'red' }}>
                                                            • {issue}
                                                        </div>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                )}


                {/* CSV Format Guide Card */}
                <div id="bulk-upload-format-guide-card" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
                    <h2 id="bulk-upload-format-guide-title" className="text-lg sm:text-xl font-bold text-gray-900 mb-6">
                        {t('formatGuide.title')}
                    </h2>

                    <div className="space-y-6 text-sm sm:text-base text-gray-700">
                        {/* Required Columns */}
                        <div id="bulk-upload-format-required-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.requiredColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">question_text</code>
                                    {' '}– {t('formatGuide.requiredColumns.questionText')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">question_type</code>
                                    {' '}– {t('formatGuide.requiredColumns.questionType')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">difficulty_tag</code>
                                    {' '}– {t('formatGuide.requiredColumns.difficultyTag')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">marks</code>
                                    {' '}– {t('formatGuide.requiredColumns.marks')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">explanation_text</code>
                                    {' '}– {t('formatGuide.requiredColumns.explanationText')}
                                </li>
                            </ul>
                        </div>

                        {/* MCQ Columns */}
                        <div id="bulk-upload-format-mcq-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.mcqColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        option_a, option_b, option_c, option_d
                                    </code>
                                    {' '}– {t('formatGuide.mcqColumns.options')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        correct_answer
                                    </code>
                                    {' '}– {t('formatGuide.mcqColumns.correctAnswer')}
                                </li>
                            </ul>
                        </div>

                        {/* MCQ Multi Columns */}
                        <div id="bulk-upload-format-mcq-multi-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.mcqMultiColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        option_a, option_b, option_c, option_d
                                    </code>
                                    {' '}– {t('formatGuide.mcqMultiColumns.options')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        correct_answers
                                    </code>
                                    {' '}– {t('formatGuide.mcqMultiColumns.correctAnswers')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        marks_per_correct
                                    </code>
                                    {' '}– {t('formatGuide.mcqMultiColumns.marksPerCorrect')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        negative_marks_per_incorrect
                                    </code>
                                    {' '}– {t('formatGuide.mcqMultiColumns.negativeMarksPerIncorrect')}
                                </li>
                            </ul>
                        </div>

                        {/* Numeric Columns */}
                        <div id="bulk-upload-format-numeric-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.numericColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        answer_type
                                    </code>
                                    {' '}– {t('formatGuide.numericColumns.answerType')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        acceptable_answers
                                    </code>
                                    {' '}– {t('formatGuide.numericColumns.acceptableAnswers')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        min_value, max_value
                                    </code>
                                    {' '}– {t('formatGuide.numericColumns.minMaxValue')}
                                </li>
                            </ul>
                        </div>

                        {/* Text Columns */}
                        <div id="bulk-upload-format-text-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.textColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        sample_answer
                                    </code>
                                    {' '}– {t('formatGuide.textColumns.sampleAnswer')}
                                </li>
                            </ul>
                        </div>

                        {/* Optional Columns */}
                        <div id="bulk-upload-format-optional-columns">
                            <h3 className="font-semibold text-gray-900 mb-3">
                                {t('formatGuide.optionalColumns.title')}
                            </h3>
                            <ul className="space-y-2">
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        negative_marks
                                    </code>
                                    {' '}– {t('formatGuide.optionalColumns.negativeMarks')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        explanation_video_url
                                    </code>
                                    {' '}– {t('formatGuide.optionalColumns.explanationVideoUrl')}
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-2 py-1 rounded text-blue-600">
                                        status
                                    </code>
                                    {' '}– {t('formatGuide.optionalColumns.status')}
                                </li>
                            </ul>
                        </div>

                        {/* Note */}
                        <div id="bulk-upload-format-note" className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                            <strong>{t('formatGuide.note.title')}:</strong>{' '}
                            {t('formatGuide.note.content')}
                        </div>
                    </div>
                </div>
            </div>


        </div>


    );
};

export default BulkImportQuestions;